/**
 * The offline runtime: what starts at boot, what refreshes, and when.
 *
 * Kept apart from `snapshot.ts` (which knows how to download) and `packs.ts`
 * (which knows what to download) so the SCHEDULE is readable in one place —
 * every timer and trigger in offline mode lives here.
 */

import { authStorage, promoteAuthPersistenceToDurable } from "@/services/authService";
import { isOffline, subscribeReachability } from "@/services/offlineSignal";
import {
  dropUnearnedPacks,
  type PackId,
  readPackFreshness,
  resolvePacksForUser,
} from "./packs";
import { currentUserKey, type PackSyncResult, syncPacks } from "./snapshot";
import { isOfflineStorageSupported, requestStoragePersistence } from "./idb";
import { activePackScope, NO_TILL_SELECTED } from "./scope";
import { watch } from "vue";

/**
 * A quiet background refresh. Three minutes is short enough that a price change
 * reaches a till within one customer's visit, and long enough that a shop full
 * of tablets is not hammering the server.
 */
const REFRESH_INTERVAL_MS = 3 * 60 * 1000;

/** Do not re-download on every focus event; a user alt-tabs constantly. */
const MIN_REFRESH_GAP_MS = 60 * 1000;

/**
 * How often to try sending queued work.
 *
 * ⚠️ THIS WAS MISSING, AND THE PROMISE DEPENDED ON IT. `drainOutbox` was only
 * called by the checkout that queued a sale and by the queue screen's "Sync now".
 * So a till that went offline, took a sale and was then left alone — or reloaded,
 * which is the normal case on a desktop till — kept that sale until somebody made
 * another sale or went looking for the queue. The app says the sale "will be sent
 * automatically when the connection returns"; this is what makes that true.
 *
 * Found by running the real Electron build offline, reloading, and watching the
 * sale sit there.
 */
const SYNC_INTERVAL_MS = 30 * 1000;

let refreshTimer: number | null = null;
let syncTimer: number | null = null;
let started = false;
let lastRefreshStartedAt = 0;
let refreshInFlight: Promise<PackRefreshResults> | null = null;
/** The one forced run queued behind `refreshInFlight`; see `refreshPacks`. */
let forcedFollowUp: Promise<PackRefreshResults> | null = null;

/**
 * What a refresh did to the copy the till actually reads.
 *
 * `null` when the POS pack's items did not land (nothing to reload), `true` when the
 * mirror was reloaded, `false` when the reload itself failed — the case that used to
 * be swallowed, leaving a downloaded catalogue the screen never showed.
 */
export type PackRefreshResults = PackSyncResult[] & { mirrorReloaded?: boolean | null };
let unsubscribeReachability: (() => void) | null = null;
let stopTillWatch: (() => void) | null = null;

export interface OfflineRuntimeStatus {
  supported: boolean;
  user: string;
  packs: PackId[];
  ready: boolean;
  lastRefreshAt: number;
}

let lastRefreshAt = 0;

export function offlineRuntimeStatus(): OfflineRuntimeStatus {
  return {
    supported: isOfflineStorageSupported(),
    user: currentUserKey(),
    packs: resolvePacksForUser(),
    ready: started,
    lastRefreshAt,
  };
}

/**
 * Send anything waiting, if there is anything and we can reach the server.
 *
 * Cheap to call often: `drainOutbox` itself refuses when offline, when another
 * tab holds the lease, and when it is already running.
 */
async function trySync(): Promise<void> {
  if (isOffline())
    return;

  try {
    const { hasUnsentWork } = await import("./outbox");

    if (!await hasUnsentWork())
      return;

    const { drainOutbox } = await import("./syncEngine");

    await drainOutbox();
  }
  catch (error) {
    // Never let a sync attempt break the screen the user is on.
    console.warn("Offline sync attempt failed:", error);
  }
}

/**
 * Refresh every pack this user has earned.
 *
 * Collapses concurrent callers onto one run: the boot hydrate, the timer, a
 * focus event and a reconnect can all fire within the same second, and four
 * simultaneous catalogue downloads on a weak link is how a tablet locks up.
 */
export async function refreshPacks(options: { force?: boolean } = {}): Promise<PackRefreshResults> {
  /**
   * ⚠️ A FORCED REFRESH MUST NOT JOIN A RUN THAT STARTED BEFORE IT WAS ASKED FOR.
   *
   * A pack download is ~24 catalogue pages read one after another, each with its own
   * stock read. Joining a timer- or focus-started run meant the Sync button handed back
   * pages read BEFORE the purchase or transfer that made the cashier press it — the
   * toast said "done" and the old quantity stayed. So a forced call waits for the
   * current run and then starts its own; concurrent forced calls share that one
   * follow-up rather than queueing a download each.
   */
  if (refreshInFlight) {
    if (!options.force)
      return refreshInFlight;

    if (!forcedFollowUp) {
      forcedFollowUp = (async () => {
        // A loop, not one await: an automatic run can start while this one waits,
        // and joining THAT would be the same stale-run problem one step later.
        while (refreshInFlight)
          await refreshInFlight.catch(() => {});

        // Cleared BEFORE starting, synchronously with it: a forced call arriving once
        // the new run is under way asked after it began, so it earns its own follow-up.
        forcedFollowUp = null;

        return refreshPacks({ force: true });
      })();
    }

    return forcedFollowUp;
  }

  if (!isOfflineStorageSupported() || isOffline())
    return [];

  const user = currentUserKey();

  if (!user)
    return [];

  const sinceLast = Date.now() - lastRefreshStartedAt;

  if (!options.force && lastRefreshStartedAt && sinceLast < MIN_REFRESH_GAP_MS)
    return [];

  const packs = resolvePacksForUser();

  if (!packs.length)
    return [];

  /**
   * ⚠️ THE IN-MEMORY GAP ABOVE DIES WITH THE PAGE, AND THAT WAS COSTING A FULL
   * DOWNLOAD PER LOAD.
   *
   * `lastRefreshStartedAt` resets on every navigation that reloads the app, so a
   * cashier reopening the till re-fetched the entire catalogue, stock and customer
   * list — measured on a warm load, where Item and Bin were still being read twice.
   * The stored `syncedAt` survives, so ask IT how long it has really been.
   *
   * `force` (a manual "Refresh data", or the first load after signing in) still
   * bypasses this, so a user who asks for fresh data always gets it.
   */
  if (!options.force) {
    // Scope-aware on purpose: another till's pack must never read as fresh for
    // this one, or the download the cashier needs is skipped.
    const freshness = await Promise.all(
      packs.map(pack => readPackFreshness(user, pack, activePackScope(pack))),
    );
    const allRecent = freshness.every(pack =>
      pack.syncedAt && Date.now() - pack.syncedAt < MIN_REFRESH_GAP_MS && pack.complete);

    if (allRecent) {
      lastRefreshStartedAt = Date.now();

      return [];
    }
  }

  lastRefreshStartedAt = Date.now();

  refreshInFlight = (async () => {
    try {
      // A user who lost a permission should not keep that data on the device.
      await dropUnearnedPacks(user);

      const results = await syncPacks(packs, { user });

      lastRefreshAt = Date.now();

      /**
       * ⚠️ A DOWNLOADED PACK IS NOT A USABLE PACK UNTIL THE MIRROR RELOADS.
       *
       * The till reads from an in-memory copy built when it opened. The pack
       * almost always lands later — a first sign-in cannot download one until a
       * cashier is active — so without this the device holds the catalogue and
       * the screen still has nothing to show from it.
       */
      /**
       * ⚠️ GATED ON THE ITEMS DATASET, NOT ON A PERFECT PACK.
       *
       * `syncPack` sets `ok: failed.length === 0`, so ONE failing dataset marked
       * the whole pack not-ok and this reload was skipped — leaving freshly
       * downloaded quantities in IndexedDB that the till could never see. A
       * cashier reliably collects 403s (Item Barcode, frappe.client.get on POS
       * Profile), so for exactly the role that matters this almost never fired,
       * and "sync does not bring the new stock" was the result.
       *
       * Payment modes or barcodes failing is no reason to withhold stock the
       * device is already holding.
       */
      const itemsLanded = results.some(result =>
        result.pack === "pos"
        && result.datasets.some(dataset => dataset.dataset === "items" && !dataset.error));

      /**
       * ⚠️ AWAITED, AND ITS FAILURE REPORTED.
       *
       * This was fire-and-forget behind `.catch(() => {})`: the Sync button's "done"
       * toast fired before the till had re-read anything, and a reload that threw left
       * the screen on the old copy with no trace anywhere. The caller now learns
       * whether the copy the till reads actually changed.
       */
      let mirrorReloaded: boolean | null = null;

      if (itemsLanded) {
        try {
          const { loadPosMirror } = await import("./posDataSource");

          mirrorReloaded = Boolean(await loadPosMirror({ force: true, scope: activePackScope("pos") }));
        }
        catch (error) {
          console.warn("Offline pack downloaded, but the till's copy could not be reloaded:", error);
          mirrorReloaded = false;
        }
      }

      // `offline` and `no-till-selected` are expected states, not faults: one
      // means there is nothing to reach, the other that there is nothing to scope
      // the data to yet. Both retry on their own.
      const failed = results.filter(result =>
        !result.ok && result.error !== "offline" && result.error !== NO_TILL_SELECTED);

      // ⚠ NAME THE DATASET AND THE REASON. "2 dataset(s) failed" sent a live
      // investigation down three wrong paths: the count is in the pack result, but
      // the message saying whether it was a 403, a timeout or a bad filter is one
      // level down, in the datasets themselves.
      if (failed.length) {
        console.warn("Some offline packs did not refresh", failed.map(result => ({
          pack: result.pack,
          error: result.error,
          datasets: result.datasets.filter(row => row.error).map(row => row.dataset + ": " + row.error),
        })));
      }

      return Object.assign(results, { mirrorReloaded });
    }
    finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Called once the user is signed in and their permissions are known.
 *
 * This is where a device becomes an offline device: it earns packs, so its login
 * is made durable and its storage is marked as worth keeping.
 */
export async function prepareOfflineForUser(): Promise<void> {
  if (!isOfflineStorageSupported())
    return;

  const user = currentUserKey();
  const packs = resolvePacksForUser();

  if (!user || !packs.length)
    return;

  // Only for users who actually work offline — a back-office account that never
  // earns a pack keeps the stricter session-only default.
  promoteAuthPersistenceToDurable();

  // Best-effort, and unavailable on plain HTTP (it needs a secure context).
  const persisted = await requestStoragePersistence();

  if (!persisted)
    console.info("The browser would not promise to keep offline data; it may be evicted under storage pressure.");

  await refreshPacks({ force: true });

  /**
   * Data alone is not enough to survive a reload: the SCREEN is a lazy chunk the
   * service worker only keeps once it has been requested. Pull the ones this user
   * works in while the connection is good. Fire-and-forget and last, so it never
   * delays the data they are waiting for.
   */
  void import("./warmRoutes")
    .then(({ warmOfflineRoutes }) => warmOfflineRoutes())
    .catch(() => {});
}

/** Has this user got usable data on the device right now? */
export async function offlineReadiness(): Promise<Array<Awaited<ReturnType<typeof readPackFreshness>>>> {
  const user = currentUserKey();

  if (!user)
    return [];

  return Promise.all(resolvePacksForUser().map(pack =>
    readPackFreshness(user, pack, activePackScope(pack))));
}

/**
 * Start the schedule. Idempotent.
 *
 * Deliberately does NOT download anything by itself at boot: a user who just
 * opened the app may be offline, mid-shift, or not yet permitted. It only sets
 * up the triggers; `prepareOfflineForUser` does the first download.
 */
export function startOfflineRuntime(): void {
  if (started || typeof window === "undefined")
    return;

  if (!isOfflineStorageSupported()) {
    console.warn("Offline mode is unavailable: this browser has no usable local storage.");

    return;
  }

  started = true;

  refreshTimer = window.setInterval(() => {
    void refreshPacks();
  }, REFRESH_INTERVAL_MS);

  /**
   * ⚠️ THE TILL BECOMING KNOWN IS A DOWNLOAD TRIGGER, NOT A DETAIL.
   *
   * On a first sign-in the pack is skipped because no till is active yet, and on a
   * switch the previous till's data is the wrong warehouse's. Either way the device
   * needs the catalogue for the till it is now working as, and `force` is right:
   * the freshness gap is about how old data is, not about which till it belongs to.
   */
  stopTillWatch = watch(() => activePackScope("pos"), (next, previous) => {
    if (next === previous || !next)
      return;

    // A refresh already in flight may be downloading the previous till. Wait for
    // it to finish, then fetch the newly selected till instead of joining it.
    void (async () => {
      if (refreshInFlight)
        await refreshInFlight;

      if (activePackScope("pos") === next)
        await refreshPacks({ force: true });
    })();
  });

  // Queued work is sent on its own schedule: it matters more, and more often,
  // than refreshing data that is merely a little old.
  syncTimer = window.setInterval(() => {
    void trySync();
  }, SYNC_INTERVAL_MS);

  // Coming back to the app is the moment stale prices matter most.
  window.addEventListener("focus", () => {
    void refreshPacks();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible")
      void refreshPacks();
  });

  // The connection returning is the other moment worth catching immediately —
  // and sending what is waiting matters more than refreshing what is stale.
  unsubscribeReachability = subscribeReachability((snapshot) => {
    if (snapshot.serverReachable && snapshot.browserOnline) {
      void trySync();
      void refreshPacks();
    }
  });

  // A signed-in user reopening the app already has permissions in storage, so
  // their packs can be brought up to date without waiting for a login.
  if (authStorage.getUsername()) {
    // ⚠️ SYNC FIRST. A till is most likely to be reopened precisely because it was
    // closed with work outstanding; that work should leave before anything else.
    void trySync();
    void refreshPacks();
  }
}

export function stopOfflineRuntime(): void {
  if (typeof window !== "undefined") {
    if (refreshTimer !== null) {
      window.clearInterval(refreshTimer);
      refreshTimer = null;
    }

    if (syncTimer !== null) {
      window.clearInterval(syncTimer);
      syncTimer = null;
    }
  }

  unsubscribeReachability?.();
  unsubscribeReachability = null;
  stopTillWatch?.();
  stopTillWatch = null;
  started = false;
}
